import React, { useMemo } from 'react'
import { DataBlock, FlowGrid, Paragraph } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { getHouseholdSuggestions } from '../utils/familyMember.utils'

/**
 * FamilyMembersReviewList
 *
 * Displays a read-only list of relationship entries.
 * Used in the review (non-edit) mode of the Family Members card to summarize selected relationships.
 *
 * Conditional Rendering:
 * - If no relationships AND reviewed=true: Shows "No family members added" message
 * - If no relationships AND reviewed=false: Renders nothing (card still pending completion)
 * - If has relationships: Shows household data + relationship list in FlowGrid
 *
 * @component
 * @param {Object} props
 * @param {Array<Object>} props.relationships Array of relationship field objects. Each item should include:
 *   - {string} id          Unique identifier (used as React key)
 *   - {string} label       Localized relationship label (e.g., 'Father', 'Mother')
 *   - {string} value       Related person's display name
 * @param {Object} props.record The SLS record object for the focused person.
 * @param {Object} props.person The SLS person object for focused person.
 * @param {boolean} props.reviewed Whether the card is in reviewed state (true) or pending state (false).
 * @param {boolean} props.isInReviewProcess Indicates if the parent panel is in review process mode for newly added people.
 * @returns {JSX.Element}
 */
const FamilyMembersReviewList = ({ relationships, record, person, reviewed, isInReviewProcess }) => {
  const [t] = useTranslation()

  const householdData = useMemo(() => {
    const household = getHouseholdSuggestions({ records: [record], person })?.[0] || {}
    const personName = household?.primaryText || ''

    const recordType = record?.typeData?.displayLabel || ''
    // 'RECORD' is the default type label for unspecified record types. We omit it for clarity.
    const label =
      recordType && recordType !== 'RECORD'
        ? t('household-details-card.recordType', 'Household • {recordType}', { recordType })
        : t('household-details-card.household-label', 'Household')

    return { label, value: personName }
  }, [record, person, t])

  // Case 1: No relationships, reviewed = false → Render nothing (intentional)
  // This is intentional: when the card hasn't been reviewed yet and has no relationships,
  // we render nothing to indicate the user hasn't completed this section.
  // But if the card is in a review process (e.g., for newly added people), we still want to show data.
  if (reviewed === false && !isInReviewProcess) {
    return null
  }

  return (
    <FlowGrid orientation="vertical" columns={1} gutters="xxs">
      {householdData.value && <DataBlock heading={householdData.label} data={householdData.value} />}

      {/* Case 2: No relationships, reviewed = true → Show "No family members added" */}
      {relationships.length === 0 && reviewed && (
        <Paragraph secondary size="sm">
          <i>{t('no-family-members-found', 'No family members added')}</i>
        </Paragraph>
      )}

      {/* Case 3: Has relationships → Show list */}
      {relationships.length > 0 &&
        relationships.map((rel) => (
          <DataBlock key={rel?.person?.id || rel?.id} heading={rel?.label} data={rel?.value} />
        ))}
    </FlowGrid>
  )
}

export default FamilyMembersReviewList
